// SPDX-License-Identifier: MIT
/**
 * Interface tokens derived from the brand and the ground, so a drawn product UI looks like this
 * brand's product on a dark or a light film. Sizes are for video: about twice a real interface, so
 * the smallest text (24) still reads in a player 1200 px wide.
 */
import { font, rgba } from '../../brand.ts';
import type { QAReporter, SceneEnv } from '../../scene.ts';
import { contrast, luminance, mix, readableOn } from '../color.ts';
import { type Theme, readableTint, theme } from '../theme.ts';
export type UiTokens = {
    theme: Theme;
    dark: boolean;
    /** Ground behind the interface. */
    bg: string;
    /** Cards and panels. */
    surface: string;
    /** One level up: popovers, toasts, the active tab. */
    raised: string;
    /** Inset: input fields, tracks. */
    sunken: string;
    /** Hairlines (rgba). */
    border: string;
    /** Text on surfaces. */
    text: string;
    /** Secondary text, ≥ 4.5:1 on `surface`. */
    muted: string;
    /** Placeholders, ≥ 3:1 on `surface`. */
    faint: string;
    /** Placeholder content bars (instead of invented text). */
    skeleton: string;
    /** Empty tracks and inactive dots: switches, sliders, rings, window buttons. */
    track: string;
    /** Brand primary for fills. */
    accent: string;
    /** Text and icons on `accent`, ≥ 4.5:1 where the palette allows. */
    onAccent: string;
    /** Accent wash for selected rows and badges. */
    accentSoft: string;
    /** Accent that reads as small text on `surface` (≥ 4.5:1). */
    accentText: string;
    /** Shadow colour and strength (0–1). */
    shadow: {
        color: string;
        strength: number;
    };
    radius: {
        sm: number;
        md: number;
        lg: number;
        pill: number;
    };
    /** Type scale in design px. Nothing smaller than `caption`. */
    type: {
        caption: 24;
        body: 28;
        title: 36;
        heading: 48;
        display: 96;
    };
    /** Control heights. */
    control: {
        sm: 56;
        md: 64;
        lg: 80;
    };
    font: (px: number, weight?: number, family?: 'display' | 'mono') => string;
    /** The scene's QA reporter (QA sessions only), so components can say where they draw. */
    report?: QAReporter;
};
/** Mix `from` towards `to` as far as possible (≤ maxK) while keeping `ratio` contrast on `bg`. */
function softest(from: string, to: string, bg: string, ratio: number, maxK: number): string {
    for (let k = maxK; k > 0; k -= 0.02) {
        const c = mix(from, to, k);
        if (contrast(c, bg) >= ratio)
            return c;
    }
    return from;
}
export function tokens(env: Pick<SceneEnv, 'brand' | 'style'> & Partial<Pick<SceneEnv, 'report'>>, { radius = 16 } = {}): UiTokens {
    const th = theme(env);
    const { ink, paper } = env.brand.colors;
    const { dark, bg, fg, primary } = th;
    // Light grounds: a near-white paper keeps white cards and lets border and shadow separate them.
    const whitePaper = luminance(paper) > 0.85;
    const surface = dark ? mix(bg, fg, 0.06) : whitePaper ? '#FFFFFF' : mix(paper, '#FFFFFF', 0.7);
    const raised = dark ? mix(bg, fg, 0.1) : '#FFFFFF';
    const sunken = dark ? mix(bg, fg, 0.03) : mix(surface, ink, 0.035);
    let onAccent = readableOn(primary, [paper, ink]);
    if (contrast(onAccent, primary) < 4.5)
        onAccent = readableOn(primary, [onAccent, '#FFFFFF', '#0A0A0A']);
    return {
        theme: th,
        dark,
        bg,
        surface,
        raised,
        sunken,
        border: rgba(fg, dark ? 0.12 : 0.1),
        text: fg,
        muted: softest(fg, surface, surface, 4.5, 0.5),
        faint: softest(fg, surface, surface, 3, 0.65),
        skeleton: mix(surface, fg, dark ? 0.11 : 0.1),
        track: mix(surface, fg, dark ? 0.18 : 0.16),
        accent: primary,
        onAccent,
        accentSoft: mix(surface, primary, dark ? 0.2 : 0.12),
        accentText: readableTint(primary, surface, fg, 4.5),
        shadow: dark ? { color: '#000000', strength: 0.55 } : { color: mix(ink, '#000000', 0.4), strength: 0.16 },
        radius: { sm: radius / 2, md: radius, lg: radius * 1.5, pill: 999 },
        type: { caption: 24, body: 28, title: 36, heading: 48, display: 96 },
        control: { sm: 56, md: 64, lg: 80 },
        font: (px, weight = 500, family = 'display') => font(env.brand, weight, px, family),
        ...(env.report ? { report: env.report } : {}),
    };
}
